'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { SectionHeading } from '@/components/frontend/common/section-heading';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { Settings, BarChart3, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WhatIsSectionProps {
    translationNamespace: string;
    variant?: 'default' | 'gradient';
    subsectionKey?: string;
    subsectionNamespace?: string;
    showTwoColumnBlocks?: boolean;
    block1Icon?: 'settings' | 'barChart';
    block2Icon?: 'settings' | 'barChart';
    pill?: string;
    sectionTitle?: string;
}

export function WhatIsSection({
    translationNamespace,
    variant = 'gradient',
    subsectionKey = 'howBitcoinsMade',
    subsectionNamespace,
    showTwoColumnBlocks = false,
    block1Icon = 'settings',
    block2Icon = 'barChart',
    pill,
    sectionTitle,
}: WhatIsSectionProps) {
    const t = useTranslations(translationNamespace);
    // Always call hooks unconditionally - use fallback if namespace not provided
    const subsectionT = useTranslations(subsectionNamespace || translationNamespace);
    const sectionRef = useScrollAnimation({ threshold: 0.1 });

    const iconMap = {
        settings: Settings,
        barChart: BarChart3,
    };

    const Icon1 = iconMap[block1Icon];
    const Icon2 = iconMap[block2Icon];

    return (
        <SectionWrapper
            variant="gradient"
            padding="lg"
            ref={sectionRef.ref}
            className="relative bg-gray-50 dark:bg-gray-950 border-t border-gray-200 dark:border-gray-900"
        >
            {/* Decorative background elements */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <div className="absolute left-1/4 top-0 h-96 w-96 rounded-full bg-primary-500/5 dark:bg-primary-500/5 blur-3xl" />
                <div className="absolute right-1/4 bottom-0 h-96 w-96 rounded-full bg-blue-500/5 dark:bg-blue-500/5 blur-3xl" />
            </div>

            <div className="container relative z-10">
                {/* Section Heading */}
                <SectionHeading
                    page=""
                    fallbackPill={pill}
                    fallbackTitle={sectionTitle}
                    align="center"
                    maxWidthClass="max-w-4xl"
                    className="mb-16"
                />

                {/* Two Column Blocks Layout */}
                {showTwoColumnBlocks && (
                    <div className="max-w-5xl mx-auto">
                        <div className="grid md:grid-cols-2 gap-8">
                            {/* Left Block - What is Bitcoin */}
                            <div
                                className={cn(
                                    "relative group transition-all duration-700",
                                    sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                                )}
                                style={{ transitionDelay: '200ms' }}
                            >
                                {/* Floating Icon Badge */}
                                <div className="absolute -top-4 -left-4 w-16 h-16 rounded-2xl bg-primary-500 dark:bg-primary-600 flex items-center justify-center shadow-xl z-10 group-hover:scale-110 transition-transform duration-300">
                                    {Icon1 && <Icon1 className="w-8 h-8 text-white" />}
                                </div>

                                {/* Main Card */}
                                <div className="relative bg-white dark:bg-gray-900 rounded-2xl p-8 pt-12 border-2 border-gray-200 dark:border-gray-800 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 overflow-hidden">
                                    {/* Decorative corner accent */}
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/10 dark:bg-primary-500/10 rounded-bl-full opacity-50"></div>

                                    {/* Content */}
                                    <div className="relative z-10">
                                        <Typography variant="h3" className="mb-3 text-gray-900 dark:text-white">
                                            {t('title')}
                                        </Typography>
                                        <Typography variant="p" className="text-gray-600 dark:text-gray-300">
                                            {t('description')}
                                        </Typography>
                                    </div>

                                    {/* Bottom accent line */}
                                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary-500 dark:bg-primary-600 opacity-30"></div>
                                </div>

                                {/* Arrow connector */}
                                <div className="hidden md:block absolute top-1/2 -right-6 transform -translate-y-1/2 z-10">
                                    <div className="w-12 h-12 rounded-full bg-white dark:bg-gray-900 border-2 border-primary-500 dark:border-primary-600 flex items-center justify-center shadow-lg">
                                        <ArrowRight className="w-6 h-6 text-primary-500 dark:text-primary-400" />
                                    </div>
                                </div>
                            </div>

                            {/* Right Block - How are Bitcoins made */}
                            <div
                                className={cn(
                                    "relative group transition-all duration-700",
                                    sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                                )}
                                style={{ transitionDelay: '350ms' }}
                            >
                                {/* Floating Icon Badge */}
                                <div className="absolute -top-4 -left-4 w-16 h-16 rounded-2xl bg-blue-500 dark:bg-blue-600 flex items-center justify-center shadow-xl z-10 group-hover:scale-110 transition-transform duration-300">
                                    {Icon2 && <Icon2 className="w-8 h-8 text-white" />}
                                </div>

                                {/* Main Card */}
                                <div className="relative bg-white dark:bg-gray-900 rounded-2xl p-8 pt-12 border-2 border-gray-200 dark:border-gray-800 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 overflow-hidden">
                                    {/* Decorative corner accent */}
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 dark:bg-blue-500/10 rounded-bl-full opacity-50"></div>

                                    {/* Content */}
                                    <div className="relative z-10">
                                        <Typography variant="h3" className="mb-3 text-gray-900 dark:text-white">
                                            {subsectionT(`${subsectionKey}.title`)}
                                        </Typography>
                                        <Typography variant="p" className="text-gray-600 dark:text-gray-300">
                                            {subsectionT(`${subsectionKey}.description`)}
                                        </Typography>
                                    </div>

                                    {/* Bottom accent line */}
                                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-500 dark:bg-blue-600 opacity-30"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </SectionWrapper>
    );
}
